import { AnimatePresence, Link, MetadataContext, ModuleBase } from '@/components';
import ReportModulHeader from '@/components/report/ReportModulHeader';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useContext, useState } from 'react';
import DropdownArrow from '@/assets/caretDownLight.svg';
import { accordionPanel } from '@/animations';

type SummaryListModuleModel = ModuleBaseModel<
  {
    blocks: {
      heading: string;
      text: string;
      anchor: {
        href: string;
        text: string;
      };
    }[];
    collapseSummary?: boolean;
  } & ModuleBasePropsModel
>;

// http://localhost:3000/beauty/sports-beauty-active-outdoor-opportunities

const SummaryListModule = (data: SummaryListModuleModel) => {
  const {
    props: { blocks, collapseSummary },
  } = data;

  const [expandedIndex, setExpandedIndex] = useState<number>(-1);

  const _setExpandedIndex = (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(-1);
    } else {
      setExpandedIndex(index);
    }
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const { pageType, isStub } = useContext(MetadataContext);
  const t = useTranslations();

  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, anchor: 'report-summary' } }}
      className="bg-gray-2 print:bg-white"
    >
      <div className="container">
        <ReportModulHeader
          title={t('general.$summary')}
          expanded={!collapseSummary}
          saveOpenStateKey="summaryListModule"
        >
          <div className="hidden justify-between gap-3 pt-12 md:flex">
            <div className="xxl:w-2/12 flex w-4/12 flex-col gap-6 lg:w-3/12">
              {blocks.map((item, index) => (
                <div key={index} className="w-full">
                  <h4
                    className={classNames(
                      'cursor-pointer transition-all duration-300',
                      activeIndex === index
                        ? 'text-lg leading-8 xl:text-[26px]'
                        : 'text-sm font-thin opacity-50 lg:text-xl lg:leading-[26px]',
                    )}
                    onClick={() => setActiveIndex(index)}
                  >
                    {item.heading}
                  </h4>
                </div>
              ))}
            </div>
            <AnimatePresence>
              <div className="flex w-8/12 flex-col gap-6 lg:w-9/12">
                <motion.div
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 0, opacity: 0 }}
                  transition={{
                    type: 'tween',
                    ease: 'easeOut',
                    duration: 0.5,
                  }}
                  key={blocks[activeIndex].heading}
                  className="flex flex-col gap-6 md:pr-12 lg:pr-16"
                >
                  <div
                    className={classNames(
                      'text-lg leading-6 xl:text-[26px] xl:leading-8',
                      '[&_a]:text-report-link [&_a]:visited:text-report-visited-link',
                      '[&_.largerText]:text-lg [&_.largerText]:leading-6 lg:[&_.largerText]:text-[32px] lg:[&_.largerText]:leading-7',
                      '[&_.subTitle]:text-lg [&_.subTitle]:leading-[22px] [&_.subTitle]:font-bold',
                    )}
                    dangerouslySetInnerHTML={{ __html: blocks[activeIndex].text }}
                  />

                  {blocks[activeIndex].anchor && pageType !== 'legacyReportPage' && !isStub && (
                    <Link className="font-weight-bold body-1 add-bold pl-0" link={blocks[activeIndex].anchor}>
                      {t('summaryList.$jumpToSection')}
                    </Link>
                  )}
                </motion.div>
              </div>
            </AnimatePresence>
          </div>
          <div className="mt-6 flex flex-col gap-6 md:hidden">
            {blocks.map((item, index) => (
              <div key={index} className="flex flex-col">
                <div className="flex items-center justify-between gap-4">
                  <button
                    className="flex cursor-pointer items-center justify-between gap-6 text-left"
                    onClick={() => _setExpandedIndex(index)}
                  >
                    <p
                      className={classNames(
                        'transition-all duration-300',
                        expandedIndex === index ? 'text-lg' : 'text-sm',
                      )}
                    >
                      {item.heading}
                    </p>

                    <DropdownArrow
                      role="presentation"
                      className={classNames(
                        'h-3.5 w-3.5 fill-black transition',
                        expandedIndex === index ? '-rotate-180' : 'rotate-0',
                      )}
                    />
                  </button>
                  {blocks[activeIndex].anchor && pageType !== 'legacyReportPage' && isStub && (
                    <Link className="shrink-0 pl-0 text-sm font-bold" link={blocks[activeIndex].anchor}>
                      {t('summaryList.$jumpToSection')}
                    </Link>
                  )}
                </div>
                <div className="overflow-hidden">
                  <motion.div
                    variants={accordionPanel.variants}
                    initial="inactive"
                    animate={expandedIndex === index ? 'active' : 'inactive'}
                    className="print:!h-auto print:!opacity-100"
                  >
                    {item.text && (
                      <div
                        className={classNames(
                          'pt-2 pb-4 text-base',
                          '[&_a]:text-report-link [&_a]:visited:text-report-visited-link',
                        )}
                        dangerouslySetInnerHTML={{ __html: item.text }}
                      />
                    )}
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
        </ReportModulHeader>
      </div>
    </ModuleBase>
  );
};

export default SummaryListModule;
